import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import './RichText.css';

/** True when the string contains HTML tags (new rich answers) rather than plain text (old answers). */
export const isHtml = (s) =>
  /<\/?(p|div|span|br|table|thead|tbody|tr|td|th|colgroup|col|ul|ol|li|h[1-6]|strong|em|b|i|u|s|sub|sup|blockquote|mark|a|img)\b[^>]*>/i.test(s || '');

const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Plain text -> paragraphs, HTML -> unchanged. */
export const toHtml = (s) => {
  if (!s) return '';
  return isHtml(s) ? s : s.split(/\n+/).map(l => `<p>${esc(l)}</p>`).join('');
};

/* Links: DOMPurify drops `target`, so set it ourselves after sanitising. Every link opens in a
   new tab with a safe rel, and nobody can sneak in their own target value. */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  }
});

/** Strips anything unsafe (scripts, event handlers, javascript: links…) but keeps tables, colours,
    fonts, sizes, links and images (including data:image/svg+xml embedded SVGs). */
export const cleanHtml = (h) => DOMPurify.sanitize(h || '', { USE_PROFILES: { html: true } });

/** True when an editor value has no text and no table / image. */
export const isRichEmpty = (h) =>
  !h || (!/<(table|img|hr)\b/i.test(h) && h.replace(/<[^>]*>/g, '').replace(/&nbsp;|\s/g, '').length === 0);

/** Renders a saved answer. Works for both new HTML answers and old plain-text ones. */
export default function RichContent({ html, className = '' }) {
  const clean = useMemo(() => cleanHtml(toHtml(html)), [html]);
  if (!clean) return null;
  return <div className={`rt-content rich-content ${className}`} dangerouslySetInnerHTML={{ __html: clean }} />;
}